import React from "react";
import { Box, Typography, Chip, Link } from "@mui/joy";
import type { JotFormElementValue } from "../types";

interface JotFormElementProps {
  element: JotFormElementValue;
}

const JotFormElement: React.FC<JotFormElementProps> = ({ element }) => {
  const renderElement = () => {
    switch (element.type) {
      case "control_head":
        return (
          <Typography
            level="h4"
            sx={{
              mb: 1,
              mt: 2,
              fontWeight: "bold",
              color: "primary.500",
            }}
          >
            {element.text}
          </Typography>
        );

      case "control_textbox":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && (
              <Typography
                level="body-md"
                sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}
              >
                {element.answer}
              </Typography>
            )}
          </Box>
        );

      case "control_textarea":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && (
              <Typography
                level="body-md"
                sx={{
                  p: 1,
                  bgcolor: "neutral.50",
                  borderRadius: "sm",
                  whiteSpace: "pre-wrap",
                }}
              >
                {element.answer}
              </Typography>
            )}
          </Box>
        );

      case "control_phone":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && (
              <Typography
                level="body-md"
                sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}
              >
                {typeof element.answer === "object"
                  ? element.answer.full || element.answer.phone
                  : element.answer}
              </Typography>
            )}
          </Box>
        );

      case "control_email":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            <Typography
              level="body-md"
              sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}
            >
              {element.answer || "N/A"}
            </Typography>
          </Box>
        );

      case "control_assignee":
        return null;

      case "control_radio":
        let answer = element.answer;
        if (typeof element.answer === "string") {
          answer = element.answer;
        } else {
          answer = element.prettyFormat;
        }

        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {answer ? (
              <Chip variant="soft" color="primary">
                {answer}
              </Chip>
            ) : (
              <Chip color="neutral">N/A</Chip>
            )}
          </Box>
        );

      case "control_checkbox":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && Array.isArray(element.answer) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {element.answer.map((item, index) => (
                  <Chip key={index} variant="soft" color="primary">
                    {item}
                  </Chip>
                ))}
              </Box>
            )}
          </Box>
        );

      case "control_dropdown":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer ? (
              <Chip variant="soft" color="primary">
                {element.answer}
              </Chip>
            ) : (
              <Chip color="neutral">N/A</Chip>
            )}
          </Box>
        );

      case "control_fullname":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && typeof element.answer === "object" && (
              <Typography
                level="body-md"
                sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}
              >
                {element.prettyFormat ||
                  `${element.answer.first || ""} ${
                    element.answer.last || ""
                  }`.trim()}
              </Typography>
            )}
          </Box>
        );

      case "control_datetime":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.prettyFormat && (
              <Typography
                level="body-md"
                sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}
              >
                {element.prettyFormat}
              </Typography>
            )}
          </Box>
        );

      case "control_fileupload":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && Array.isArray(element.answer) && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {element.answer.map((url, index) => {
                  // If the url ends with .jpg or .png, render as an <img> instead of a link
                  const lowerUrl = url.toLowerCase();
                  if (lowerUrl.endsWith(".jpg") || lowerUrl.endsWith(".png")) {
                    return (
                      <Box
                        key={index}
                        sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}
                      >
                        <img
                          src={url}
                          alt={`uploaded file ${index + 1}`}
                          style={{
                            maxWidth: "100%",
                            maxHeight: 300,
                            display: "block",
                          }}
                        />
                        <Link
                          href={`https://green-truth-d4cf.spectre-c.workers.dev/${url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ mt: 0.5, display: "inline-block" }}
                        >
                          {url.split("/").pop() || "ดูไฟล์"}
                        </Link>
                      </Box>
                    );
                  }
                  return (
                    <Link
                      key={index}
                      href={`https://green-truth-d4cf.spectre-c.workers.dev/${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {url.split("/").pop() || "ดูไฟล์"}
                    </Link>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      case "control_number":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            <Typography
              level="body-md"
              sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}
            >
              {element.answer}
            </Typography>
          </Box>
        );
      case "control_image":
        return null;

      case "control_widget":
        let parsedAnswer = null;
        let answerHtml = element.answer;
        if (typeof element.answer === "string") {
          try {
            parsedAnswer = JSON.parse(element.answer);
            if (parsedAnswer?.widget_metadata?.type === "imagelinks") {
              answerHtml = parsedAnswer.widget_metadata.value.map(
                (item: any) =>
                  `<img style="max-height: 300px;" src="https://www.jotform.com${item.url}" />`
              );
            } else if (Array.isArray(parsedAnswer)) {
              answerHtml = parsedAnswer
                .map((item: any) =>
                  Object.entries(item)
                    .map(([k, v]) => `<b>${k}</b>: ${v}`)
                    .join(", ")
                )
                .join("<br />");
            } else {
            }
          } catch (e) {
            answerHtml = answerHtml.split("\n").join("<br />");
          }
        }

        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && (
              <Box sx={{ p: 1, bgcolor: "neutral.50", borderRadius: "sm" }}>
                <Typography level="body-md">
                  <div dangerouslySetInnerHTML={{ __html: answerHtml }} />
                </Typography>
              </Box>
            )}
          </Box>
        );

      case "control_text":
        return (
          <Box sx={{ mb: 2 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <style>
                {`
                      div[role="jotform-control-text"] img {
                        display: none;
                      }
                    `}
              </style>
              <div
                role="jotform-control-text"
                // dangerouslySetInnerHTML={{ __html: element.text }}
              />
            </div>
          </Box>
        );

      case "control_scale":
        return (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-md" sx={{ mb: 0.5, fontWeight: "bold" }}>
              {element.text}
            </Typography>
            {element.answer && (
              <Typography level="body-md">
                {element.answer} เต็ม {element.scaleAmount}
              </Typography>
            )}
          </Box>
        );

      case "control_divider":
        return (
          <Box
            sx={{ borderBottom: "1px solid", borderColor: "divider", my: 2 }}
          />
        );

      case "control_pagebreak":
        return (
          <Box
            sx={{
              borderBottom: "2px solid",
              borderColor: "primary.200",
              my: 3,
            }}
          />
        );

      case "control_button":
        return null;

      default:
        // For unknown types, display raw data for debugging
        return (
          <Box sx={{ mb: 2, p: 1, bgcolor: "warning.50", borderRadius: "sm" }}>
            <Typography level="body-sm" sx={{ fontWeight: "medium", mb: 0.5 }}>
              Unknown type: {element.type}
              <pre>{JSON.stringify(element, null, 2)}</pre>
            </Typography>
          </Box>
        );
    }
  };

  return <>{renderElement()}</>;
};

export default JotFormElement;
